import { authFetch, fetchWithAuth, BASE_BACKEND_URL } from '../auth'
import React, { useState, useContext, useEffect, useCallback, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import PromoIndexView from './PromoIndexView'
import usePagination from '../layout/usePagination'
import { DEFAULT_PER_PAGE } from '../constants/constant'
import LoadingScreen from '../components/LoadingScreen'
import {
  buildDefaultClientQualifierDic,
  buildClientQualifierDicFromFilterData
} from './constants/promoQualifiers'

const PromoIndexApp = () => {
  const location = useLocation()
  const successCreateForNewPromo = location.state?.successCreateForNewPromo || false

  const [promoData, setPromoData] = useState(null)
  const [filterData, setFilterData] = useState(null)
  const [promoProductsData, setPromoProductsData] = useState(null)
  const [filterQuery, setFilterQuery] = useState(null)
  const [searchQuery, setSearchQuery] = useState(null)
  const [dateQuery, setDateQuery] = useState(null)
  const [title, setTitle] = useState('Promociones')
  const [isFetching, setIsFetching] = useState(true)
  const [isFetchingFilter, setIsFetchingFilter] = useState(true)
  const { pagination, setPagination, handlePaginationClick } = usePagination({
    per_page: DEFAULT_PER_PAGE
  })
  const [clientQualifierDic, setClientQualifierDic] = useState(buildDefaultClientQualifierDic())

  const abortControllerRef = useRef(new AbortController())

  useEffect(() => {
    const handleBeforeUnload = () => {
      abortControllerRef.current.abort('Fetch aborted by user (before unload).')
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      abortControllerRef.current.abort('Cleanup abort on unmount.')
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  useEffect(() => {
    async function fetchAll() {
      await fetchFilterInfoFromServer()
    }
    fetchAll()
  }, [])

  useEffect(() => {
    const fetchAll = async () => {
      await fetchDataFromServer()
    }
    fetchAll()
  }, [pagination.current_page, filterQuery, searchQuery, dateQuery])

  const fetchDataFromServer = useCallback(async () => {
    setIsFetching(true)
    setPromoData([])

    const per_page = pagination.per_page
    const page = pagination.current_page
    try {
      const response = await authFetch(
        `${BASE_BACKEND_URL}/promos?page=${page}&per_page=${per_page}${
          filterQuery || dateQuery || searchQuery ? '&filter_search=true' : ''
        }${filterQuery ? filterQuery : ''}${dateQuery ? dateQuery : ''}${searchQuery ? searchQuery : ''}`,
        {
          method: 'GET',
          signal: abortControllerRef.current.signal,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      )
      if (response.status === 401) return console.error(response)
      const data = await response.json()
      setPagination((prev) => ({
        ...prev,
        total_pages: Math.ceil(data['count'] / prev.per_page)
      }))
      setPromoData(data['table'])
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Error:', error)
    } finally {
      setIsFetching(false)
    }
  }, [pagination.current_page, filterQuery, searchQuery, dateQuery])

  const fetchFilterInfoFromServer = async (loadingState = true) => {
    if (loadingState) setIsFetchingFilter(true)
    try {
      const response = await fetchWithAuth(
        `${BASE_BACKEND_URL}/promos?filter_info=true`,
        {
          method: 'GET',
          signal: abortControllerRef.current.signal,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      )

      if (!response) return
      const data = response
      setFilterData(data)

      const products = data['products']
      const clients = data['clients']

      setClientQualifierDic(buildClientQualifierDicFromFilterData(data, clients))
      setPromoProductsData(products?.map((p) => p.name))
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Error:', error)
    } finally {
      setIsFetchingFilter(false)
    }
  }

  const isInitialLoading =
    (isFetching || isFetchingFilter) &&
    (promoData === null || filterData === null)

  if (isInitialLoading) {
    return <LoadingScreen text="Cargando..." />
  }

  return (
    <PromoIndexView
      promoData={promoData}
      filterData={filterData}
      promoProductsData={promoProductsData}
      pagination={pagination}
      handlePaginationClick={handlePaginationClick}
      dateQuery={dateQuery}
      searchQuery={searchQuery}
      filterQuery={filterQuery}
      setDateQuery={setDateQuery}
      setSearchQuery={setSearchQuery}
      setFilterQuery={setFilterQuery}
      isFetching={isFetching}
      isFetchingFilter={isFetchingFilter}
      title={title}
      successCreateForNewPromo={successCreateForNewPromo}
      clientQualifierDic={clientQualifierDic}
      setPagination={setPagination}
    />
  )
}

export default PromoIndexApp
